import * as THREE from 'three/webgpu';
import type { AssemblyTransformHistoryRecord } from '../../../assembly/component-instance';
import { isFeatureResultMesh } from '../../../mesh/feature';
import { resolveImportedModelTopologyRepository } from '../../../imported-model/topology/ImportedModelTopologyRepository';
import type { SketchEntityReferenceTopologyOwnerQueryInput } from '../../../reference/sketch-entity-reference/repository/SketchEntityReferenceRepository';
import type { FeatureTimelineRebuildApplyInput } from '../../../../types/feature/FeatureTimelineRebuild';
import type {
	FeatureTimelineRebuildRuntimeDependencies,
} from '../../../../types/viewer/FeatureTimelineRebuildRuntime';

export type {
	FeatureTimelineRebuildRuntimeDependencies,
} from '../../../../types/viewer/FeatureTimelineRebuildRuntime';

/**
 * Feature Timeline 在 Viewer 侧的 topology 与结果应用运行时。
 *
 * 唯一队列和提交权仍属于 FeatureRebuildOrchestrator；本模块只负责把被冻结的结果
 * 投影到场景、Rollback 时间线和 Convert Entities 诊断，且不保存第二份命令历史。
 */
export class FeatureTimelineRebuildRuntime {
	/** @param dependencies Viewer 提供的场景与投影能力。 */
	public constructor(
		private readonly dependencies: FeatureTimelineRebuildRuntimeDependencies,
	) {}

	/** 从正式结果体解析稳定 topology owner 查询输入。 */
	public resolveTopologyOwnerFromResultMesh(
		mesh: THREE.Mesh | null,
		options: { readonly matchAnyBodyBindingWhenMissing?: boolean } = {},
	): SketchEntityReferenceTopologyOwnerQueryInput | null {
		if (!mesh || !isFeatureResultMesh(mesh)) {
			return null;
		}
		const metadata = mesh.getFeatureMetadata();
		const bodyBindingId = typeof metadata.bodyBindingId === 'string'
			? metadata.bodyBindingId
			: undefined;
		const appendBodyBinding = (
			owner: Pick<SketchEntityReferenceTopologyOwnerQueryInput, 'ownerKind' | 'ownerId'>,
		): SketchEntityReferenceTopologyOwnerQueryInput => {
			if (bodyBindingId) {
				return { ...owner, bodyBindingId };
			}
			return options.matchAnyBodyBindingWhenMissing === true
				? { ...owner, matchAnyBodyBinding: true }
				: owner;
		};

		if (typeof metadata.featureId === 'string' && metadata.featureId.length > 0) {
			return appendBodyBinding({
				ownerKind: 'feature-result',
				ownerId: metadata.featureId,
			});
		}
		if (
			(metadata.topologyOwnerKind === 'model-body'
				|| metadata.topologyOwnerKind === 'imported-model-body')
			&& typeof metadata.topologyOwnerId === 'string'
			&& metadata.topologyOwnerId.length > 0
		) {
			return appendBodyBinding({
				ownerKind: metadata.topologyOwnerKind,
				ownerId: metadata.topologyOwnerId,
			});
		}
		return null;
	}

	/** 从装配变换记录递归收集并去重受影响 topology owner。 */
	public resolveTopologyOwnersFromAssemblyTransform(
		record: AssemblyTransformHistoryRecord,
	): SketchEntityReferenceTopologyOwnerQueryInput[] {
		const ownerMap = new Map<string, SketchEntityReferenceTopologyOwnerQueryInput>();
		this.flattenAssemblyTransformHistoryRecords(record).forEach((currentRecord) => {
			const movedObject = this.resolveAssemblyTransformSnapshotObject(currentRecord.after);
			if (movedObject) {
				this.collectTopologyOwnerQueryInputsFromObjectTree(movedObject, ownerMap);
			}
		});
		return [...ownerMap.values()];
	}

	/** 应用已由唯一队列确认的成功或 stopped 前缀。 */
	public async applyResult(input: FeatureTimelineRebuildApplyInput): Promise<void> {
		if (
			(input.result.status !== 'success' && input.result.status !== 'stopped')
			|| !input.result.summary
		) {
			throw new Error(`不能应用未完成的时间线重建结果：${input.result.status}`);
		}
		this.dependencies.replaceRollbackTimeline(input);
		this.dependencies.syncRollbackTimeline();
		await this.dependencies.syncAffectedMeshes(input);
		this.dependencies.applyNonFeatureSideEffects();
		this.dependencies.setTimelineDegraded(input.result.status === 'stopped');
		this.dependencies.refreshReferenceDiagnostics(input.result.changedSketchIds);
		this.dependencies.notifyReferenceDiagnosticsChanged(input.result.changedSketchIds);
	}

	/** 展平主记录及关联变换记录。 */
	private flattenAssemblyTransformHistoryRecords(
		record: AssemblyTransformHistoryRecord,
	): AssemblyTransformHistoryRecord[] {
		return [
			record,
			...(record.relatedRecords ?? []).flatMap(
				(relatedRecord) => this.flattenAssemblyTransformHistoryRecords(relatedRecord),
			),
		];
	}

	/** 按组件快照定位当前场景中的组件根对象。 */
	private resolveAssemblyTransformSnapshotObject(
		snapshot: AssemblyTransformHistoryRecord['after'],
	): THREE.Object3D | null {
		const directHit = this.dependencies.getSceneObjectById(snapshot.componentInstanceId)
			?? this.dependencies.getSceneObjectById(snapshot.sourceNodeId);
		if (directHit) {
			return directHit;
		}
		let matchedObject: THREE.Object3D | null = null;
		this.dependencies.sceneRoot.traverse((object) => {
			if (matchedObject) {
				return;
			}
			const assemblyNodeId = typeof object.userData?.assemblyNodeId === 'string'
				? object.userData.assemblyNodeId
				: '';
			if (
				assemblyNodeId === snapshot.componentInstanceId
				|| assemblyNodeId === snapshot.sourceNodeId
			) {
				matchedObject = object;
			}
		});
		return matchedObject;
	}

	/** 从组件对象子树收集 topology owner。 */
	private collectTopologyOwnerQueryInputsFromObjectTree(
		rootObject: THREE.Object3D,
		ownerMap: Map<string, SketchEntityReferenceTopologyOwnerQueryInput>,
	): void {
		rootObject.traverse((object) => {
			const resultMeshOwner = this.resolveTopologyOwnerFromResultMesh(
				object instanceof THREE.Mesh ? object : null,
				{ matchAnyBodyBindingWhenMissing: true },
			);
			if (resultMeshOwner) {
				ownerMap.set(this.buildTopologyOwnerKey(resultMeshOwner), resultMeshOwner);
			}

			const repository = resolveImportedModelTopologyRepository(object);
			if (!(object instanceof THREE.Mesh) || !repository) {
				return;
			}
			repository.resolveBodyRecordsByVisibleMesh(object).forEach((bodyRecord) => {
				const owner: SketchEntityReferenceTopologyOwnerQueryInput = {
					ownerKind: 'imported-model-body',
					ownerId: bodyRecord.bodyBindingId,
					bodyBindingId: bodyRecord.bodyBindingId,
				};
				ownerMap.set(this.buildTopologyOwnerKey(owner), owner);
			});
		});
	}

	/** 构建当前批次内的稳定 owner 去重键。 */
	public buildTopologyOwnerKey(
		owner: SketchEntityReferenceTopologyOwnerQueryInput,
	): string {
		const bodyBindingKey = owner.matchAnyBodyBinding === true
			? '*'
			: owner.bodyBindingId ?? '';
		return `${owner.ownerKind}:${owner.ownerId}:${bodyBindingKey}`;
	}
}
